<script module lang="ts">
  import type { Snippet } from 'svelte';
  import type { IconProps } from '../icons/icons.types';

  export const allowedTags = ['li', 'div'] as const;
  export type AllowedTag = (typeof allowedTags)[number];

  export interface Props {
    icon?: IconProps['icon'];
    tag?: AllowedTag;
    href?: string;
    active?: boolean;
    alarm?: boolean;
    count?: number;
    separated?: boolean;
    onselect?: () => void;
    children?: Snippet;
  }
</script>

<script lang="ts">
  import { getContext } from 'svelte';
  import * as Icons from '@lucide/svelte';
  import IconComponent from '../icons/Icon.svelte';
  import { PointService } from '../points/PointService';
  import { CLOSE_CONTEXT_KEY, type CloseFn } from './dropdown.types';

  const { icon, tag, href, active, alarm, count, separated, onselect, children }: Props = $props();

  // -
  const DefaultTag = allowedTags[0];
  const ElementTag = $derived(tag && allowedTags.includes(tag) ? tag : DefaultTag);

  // -
  const Link = $derived(PointService.buildLink(href));
  const CanClick = $derived(Link.hasLink || onselect !== undefined);

  // -
  const DropdownIcon = $derived(icon ?? (Link.target ? Icons.ExternalLink : Icons.FlagTriangleRight));
  const IsActive = $derived(active ?? false);
  const IsSeparated = $derived(separated ?? false);

  // -
  const closeDropdown = getContext<CloseFn | undefined>(CLOSE_CONTEXT_KEY);

  function handleSelect(): void {
    onselect?.();
    closeDropdown?.();
  }
</script>

<svelte:element this={ElementTag} class="dropdownPoint {IsActive ? 'active' : ''} {IsSeparated ? 'separated' : ''}">
  <span class="dropdownWrapper {CanClick ? 'canClick' : ''}">
    {#if Link.hasLink}
      <a class="dropdownLink" href={Link.url} target={Link.target} rel={Link.rel} aria-current={IsActive ? 'true' : undefined} onclick={handleSelect}>
        <IconComponent icon={DropdownIcon} size={1.15} {alarm} {count} {children} />
      </a>
    {:else}
      <button type="button" class="dropdownLink" aria-current={IsActive ? 'true' : undefined} onclick={handleSelect}>
        <IconComponent icon={DropdownIcon} size={1.15} {alarm} {count} {children} />
      </button>
    {/if}
  </span>
</svelte:element>

<style lang="scss">
  @use '../../../styles/display';

  .dropdownPoint {
    &,
    & > .dropdownWrapper,
    & > .dropdownWrapper > .dropdownLink {
      @include display.block;
      font-size: var(--dropdown-font-size, 1em);
      line-height: 1;
      white-space: nowrap;
      position: relative;
      cursor: default;
    }

    :global(+ .dropdownPoint) {
      margin-top: var(--dropdown-margin-top, 0);
    }

    &.separated {
      margin-top: calc(var(--outset-abs) / 4);
      padding-top: calc(var(--outset-abs) / 4);
      border-top: var(--border-basic);
    }

    > .dropdownWrapper {
      width: 100%;
      border-radius: var(--dropdown-border-radius, 0);
      background-color: var(--dropdown-bg, transparent);

      &.canClick {
        cursor: pointer;

        > .dropdownLink {
          cursor: pointer;
        }
      }

      > .dropdownLink {
        width: 100%;
        padding: 0.5em 0.6em;
      }
    }

    &.active {
      > .dropdownWrapper {
        background-color: var(--dropdown-bg-active, var(--layout-mark-bg-full)) !important;
      }
    }

    &:hover {
      > .dropdownWrapper {
        background-color: var(--dropdown-bg-inactive, var(--layout-mark-bg-inactive));

        &.canClick {
          background-color: var(--dropdown-bg-active, var(--layout-mark-bg-light));
        }
      }
    }
  }
</style>
